import { orchid_decrypt } from "utils/OrchidEncryption";

export type DriveEntryType = "file" | "directory";

export type FileStatus =
  | "data-upload"
  | "finalize-pending"
  | "available"
  | "marked-deleted"
  | "upload-abandoned";
export type DirectoryStatus =
  | "finalize-pending"
  | "available"
  | "marked-deleted"
  | "upload-abandoned";

export type DriveEntryError =
  | "invalid drive entry identifier"
  | "invalid move across drives"
  | "not found"
  | "invalid move into subdirectory"
  | "parent directory to move to is deleted";

export type DriveEntryErrorById = { [key: string]: DriveEntryError };

export type OrchidDriveEntryKeys = {
  entryId: string;
  dataCipherKey: CryptoKey;
  metaDataCipherKey: CryptoKey;
};

export type OrchidDrivePath = string[];

export type APIOrchidDriveFile = {
  file_data_id: string;
  storage_drive: StorageDrive;
  name_encrypted: string;
  parent_directory_id: string | null;
  patient_user_id: string | null;
  file_status: FileStatus;
  create_datetime: string;
  modify_datetime: string;
  total_size?: number;
  mime_type?: string;
  deleted_datetime: string | null;
  upload_abandoned_datetime: string | null;
  upload_finalized_datetime: string | null;
  upload_started_datetime: string | null;
  uploaded_size: number;
  uploaded_chunks: number;
  chunk_size: number;
  last_uploaded_chunk_num: number | null;
  expected_chunk_count: number | null;
  upload_task_id: string;
};

export type OrchidDriveFile = {
  fileDataId: string;
  storageDrive: StorageDrive;
  name: string;
  path?: OrchidDrivePath; // undefined when full path is not known yet, supposed to be available at later time
  parentDirectoryId: string | null;
  patientUserId?: string;
  fileStatus: FileStatus;
  createDatetime: Date;
  modifyDatetime: Date;
  totalSize?: number;
  mimeType?: string;
  deletedDatetime?: Date;
  uploadAbandonedDatetime?: Date;
  uploadFinalizedDatetime?: Date;
  uploadStartedDatetime?: Date;
  uploadedSize: number;
  uploadedChunks: number;
  chunkSize: number;
  lastUploadedChunkNum?: number;
  expectedChunkCount?: number;
  uploadPercent?: number;
  uploadTaskId: string;
};

export async function orchidDriveFileFromAPI(
  data: APIOrchidDriveFile,
  metaDataCipherKey: CryptoKey,
): Promise<OrchidDriveFile> {
  return {
    fileDataId: data.file_data_id,
    storageDrive: data.storage_drive,
    name: await orchid_decrypt(data.name_encrypted, metaDataCipherKey),
    path: undefined,
    parentDirectoryId: data.parent_directory_id
      ? data.parent_directory_id
      : null,
    patientUserId: data.patient_user_id ? data.patient_user_id : undefined,
    fileStatus: data.file_status,
    createDatetime: new Date(data.create_datetime),
    modifyDatetime: new Date(data.modify_datetime),
    totalSize:
      typeof data.total_size === "number" ? data.total_size : undefined,
    mimeType: data.mime_type ? data.mime_type : undefined,
    deletedDatetime: data.deleted_datetime
      ? new Date(data.deleted_datetime)
      : undefined,
    uploadAbandonedDatetime: data.upload_abandoned_datetime
      ? new Date(data.upload_abandoned_datetime)
      : undefined,
    uploadFinalizedDatetime: data.upload_finalized_datetime
      ? new Date(data.upload_finalized_datetime)
      : undefined,
    uploadStartedDatetime: data.upload_started_datetime
      ? new Date(data.upload_started_datetime)
      : undefined,
    uploadedSize: data.uploaded_size,
    chunkSize: data.chunk_size,
    uploadedChunks: data.uploaded_chunks,
    lastUploadedChunkNum: data.last_uploaded_chunk_num || undefined,
    expectedChunkCount: data.expected_chunk_count || undefined,
    uploadPercent:
      data.last_uploaded_chunk_num !== null && data.expected_chunk_count
        ? Math.min(
            ((data.last_uploaded_chunk_num + 1) * 100) /
              data.expected_chunk_count,
            100,
          )
        : undefined,
    uploadTaskId: data.upload_task_id,
  };
}

export type APIOrchidDriveDirectory = {
  directory_id: string;
  storage_drive: StorageDrive;
  name_encrypted: string;
  create_datetime: string;
  modify_datetime: string;
  parent_directory_id: string | null;
  patient_user_id: string | null;
  directory_status: DirectoryStatus;
  deleted_datetime: string | null;
  upload_abandoned_datetime: string | null;
  upload_finalized_datetime: string | null;
  upload_task_id: string;
};

export type OrchidDriveDirectory = {
  directoryId: string;
  storageDrive: StorageDrive;
  name: string;
  path?: OrchidDrivePath; // undefined when full path is not known yet, supposed to be available at later time
  createDatetime: Date;
  modifyDatetime: Date;
  parentDirectoryId: string | null;
  patientUserId?: string;
  directoryStatus: DirectoryStatus;
  deletedDatetime?: Date;
  uploadAbandonedDatetime?: Date;
  uploadFinalizedDatetime?: Date;
  uploadTaskId: string;
};

export async function orchidDriveDirectoryFromAPI(
  data: APIOrchidDriveDirectory,
  metaDataCipherKey: CryptoKey,
): Promise<OrchidDriveDirectory> {
  return {
    directoryId: data.directory_id,
    storageDrive: data.storage_drive,
    name: await orchid_decrypt(data.name_encrypted, metaDataCipherKey),
    path: undefined,
    createDatetime: new Date(data.create_datetime),
    modifyDatetime: new Date(data.modify_datetime),
    parentDirectoryId: data.parent_directory_id
      ? data.parent_directory_id
      : null,
    directoryStatus: data.directory_status,
    patientUserId: data.patient_user_id ? data.patient_user_id : undefined,
    deletedDatetime: data.deleted_datetime
      ? new Date(data.deleted_datetime)
      : undefined,
    uploadAbandonedDatetime: data.upload_abandoned_datetime
      ? new Date(data.upload_abandoned_datetime)
      : undefined,
    uploadFinalizedDatetime: data.upload_finalized_datetime
      ? new Date(data.upload_finalized_datetime)
      : undefined,
    uploadTaskId: data.upload_task_id,
  };
}

export type APIOrchidDriveUploadTask = {
  upload_task_id: string;
  create_datetime: string;
  modify_datetime: string;
  upload_abandoned_datetime: string | null;
  upload_marked_finalized_datetime: string | null;
  upload_finalized_datetime: string | null;
  uploading_user_agent: string | null;
  uploading_orchid_unique_device_id: string | null;
  total_expected_size: number | null;
  files: string[];
  directories: string[];
};

export type OrchidDriveUploadTask = {
  uploadTaskId: string;
  createDatetime: Date;
  modifyDatetime: Date;
  uploadAbandonedDatetime?: Date;
  uploadMarkedFinalizedDatetime?: Date;
  uploadFinalizedDatetime?: Date;
  uploadingUserAgent?: string;
  uploadingOrchidUniqueDeviceId?: string;
  totalExpectedSize?: number;
  fileIds: string[];
  directoryIds: string[];
};

export type OrchidDriveUploadTaskWithEntries = OrchidDriveUploadTask & {
  files: OrchidDriveFile[];
  directories: OrchidDriveDirectory[];
  uploadPercent?: number;
};

export function orchidDriveUploadTaskFromAPI(
  data: APIOrchidDriveUploadTask,
): OrchidDriveUploadTask {
  return {
    uploadTaskId: data.upload_task_id,
    createDatetime: new Date(data.create_datetime),
    modifyDatetime: new Date(data.modify_datetime),
    uploadAbandonedDatetime: data.upload_abandoned_datetime
      ? new Date(data.upload_abandoned_datetime)
      : undefined,
    uploadMarkedFinalizedDatetime: data.upload_marked_finalized_datetime
      ? new Date(data.upload_marked_finalized_datetime)
      : undefined,
    uploadFinalizedDatetime: data.upload_finalized_datetime
      ? new Date(data.upload_finalized_datetime)
      : undefined,
    uploadingUserAgent:
      data.uploading_user_agent === null
        ? undefined
        : data.uploading_user_agent,
    uploadingOrchidUniqueDeviceId:
      data.uploading_orchid_unique_device_id === null
        ? undefined
        : data.uploading_orchid_unique_device_id,
    totalExpectedSize:
      data.total_expected_size === null ? undefined : data.total_expected_size,
    fileIds: data.files,
    directoryIds: data.directories,
  };
}

export type OrchidDriveData = {
  files: { [key: string]: OrchidDriveFile };
  directories: { [key: string]: OrchidDriveDirectory };
};

export type UsedPatientChart = {
  patientUserId: string;
  directoryId?: string | null;
  pages: number[];
};

export type UsedNoteAttachment = {
  patientUserId: string;
  directoryId?: string | null;
  pages: number[];
};

export type UsedMemberDrive = {
  directoryId?: string | null;
  pages: number[];
};

export class FileEntry {
  #_name;
  #_file;
  #_size: number = 0;

  constructor(file: File, override: Partial<File> = {}) {
    this.#_name = override.name ?? file.name;
    this.#_file = file;
    this.#_size = override.size ?? file.size;
  }

  get name() {
    return this.#_name;
  }
  get file() {
    return this.#_file;
  }
  get size() {
    return this.#_size;
  }
}

export type FileUploadEntry = {
  file: FileEntry;
  baseDirectoryId: string | null;
  pathToCreate?: string[];
};

export class UploadEntry {
  #_storageDrive;
  #_patientUserId;
  #_files;

  constructor(
    storageDrive: StorageDrive,
    patientUserId: string | null,
    files: FileUploadEntry[],
  ) {
    this.#_storageDrive = storageDrive;
    this.#_patientUserId = patientUserId;
    this.#_files = files;
  }

  get storageDrive(): StorageDrive {
    return this.#_storageDrive;
  }
  get patientUserId(): string | null {
    return this.#_patientUserId;
  }
  get files(): Array<FileUploadEntry> {
    return this.#_files;
  }
}
